// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@ces/App/Components/Notify";
import { FulfillmentMethod } from "@ces/XDL/types";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import { BaseModel, UnitOfMeasureModel, WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import { ButtonProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import {
    Button,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableRow,
    GridContainer,
    GridItem,
    LoadingOverlay,
    Select,
} from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { SelectProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEffect, useMemo } from "react";
import React, { createRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    warehouseInfo: state.context.session?.pickUpWarehouse,
    isPickup: getSession(state).fulfillmentMethod !== FulfillmentMethod.Ship,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    updatePickUpWarehouse,
};

interface OwnProps {
    productId: string;
    unitOfMeasure: string;
    defaultUom?: string;
    modalIsOpen: boolean;
    modalCloseHandler: () => void;
    extendedStyles?: ProductAvailabilityModalStyles;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductAvailabilityModalStyles {
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: InjectableCss;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderPresentationProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellProps;
    availabilityByWarehouseNameCell?: DataTableCellProps;
    warehouseStateSelection?: SelectProps;
    currentWarehouseLabel?: TypographyPresentationProps;
    currentWarehouseValue?: TypographyPresentationProps;
    inventoryInfoContainer?: GridContainerProps;
    statusSelectionContainer?: GridItemProps;
    currentWarehouseInfoContainer?: InjectableCss;
    actionButton?: ButtonProps;
    tableContainer?: InjectableCss;
    scrollContainer?: InjectableCss;
    notify?: InjectableCss;
}

export const productAvailabilityModalStyles: ProductAvailabilityModalStyles = {};

type XWarehouseDto = WarehouseDto & BaseModel;
type TStockFilter = "IN_STOCK" | "ALL";
const ProductAvailabilityModal = ({
    productId,
    unitOfMeasure,
    modalIsOpen,
    extendedStyles,
    warehouseInfo,
    modalCloseHandler,
    getRealTimeWarehouseInventory,
    updatePickUpWarehouse,
    isPickup,
    defaultUom,
}: Props) => {
    const styles = useMemo(() => mergeToNew(productAvailabilityModalStyles, extendedStyles), []);
    const [isLoading, setIsLoading] = useState(false);
    const [warehouses, setWarehouses] = useState<XWarehouseDto[]>([]);
    const [stockStatus, setStockStatus] = useState<TStockFilter>("IN_STOCK");
    const [errorMessage, setErrorMessage] = useState("");

    const parentBoxRef = createRef<HTMLDivElement>();
    const filteredWarehouses = useMemo(() => {
        const sortedWarehouses = (
            stockStatus === "ALL" ? warehouses : warehouses.filter(warehouse => warehouse.qty > 0)
        )
            .map(
                warehouse =>
                    ({
                        ...warehouse,
                        properties: {
                            ...warehouse.properties,
                            distanceFromCurrentWarehouse:
                                warehouse.properties.warehouseId === warehouseInfo?.id
                                    ? "0"
                                    : warehouse.properties.distanceFromCurrentWarehouse.replace(" mi", ""),
                        },
                    } as XWarehouseDto),
            )
            .sort((w1, w2) => {
                // make sure selected warehouse comes first
                const d1 =
                    w1.properties.warehouseId === warehouseInfo?.id
                        ? "-1"
                        : w1.properties?.distanceFromCurrentWarehouse;
                const d2 =
                    w2.properties.warehouseId === warehouseInfo?.id
                        ? "-1"
                        : w2.properties?.distanceFromCurrentWarehouse;

                let c1 = parseFloat(d1);
                let c2 = parseFloat(d2);
                if (Number.isNaN(c1)) {
                    c1 = 999999;
                }
                if (Number.isNaN(c2)) {
                    c2 = 999999;
                }
                if (c1 < c2) {
                    return -1;
                }
                if (c1 > c2) {
                    return 1;
                }
                return 0;
            });

        return sortedWarehouses;
    }, [warehouses, stockStatus]);

    const loadAvailability = () => {
        setIsLoading(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure: defaultUom || unitOfMeasure,
            expand: ["warehouses"],
            onComplete(result: any) {
                setErrorMessage(result.errorMessage);
                setWarehouses((result.warehouses || []) as XWarehouseDto[]);
                setIsLoading(false);
            },
        });
    };

    const cleanupAvailability = () => {
        setWarehouses([]);
        setStockStatus("IN_STOCK");
        setErrorMessage("");
        setIsLoading(false);
    };

    const assignAlternateWarehouse = (warehouseId?: string) => {
        if (!warehouseId) {
            return;
        }
        setIsLoading(true);
        updatePickUpWarehouse({
            pickUpWarehouse: {
                id: warehouseId,
            } as any,
            onComplete: () => {
                modalCloseHandler();
                setIsLoading(false);
            },
        });
    };

    useEffect(() => {
        if (modalIsOpen) {
            loadAvailability();
        } else {
            cleanupAvailability();
        }
        // In case we change current warehouse refetch data
    }, [modalIsOpen, warehouseInfo?.name]);

    return (
        <Modal
            {...styles.availabilityByWarehouseModal}
            headline={translate("Warehouse Availability")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
        >
            <LoadingOverlay loading={isLoading} id="product-availability-loading-container">
                {warehouseInfo && !!warehouseInfo.name && (
                    <StyledWrapper {...styles.currentWarehouseInfoContainer}>
                        <Typography {...styles.currentWarehouseLabel}>{translate("Current Warehouse")}:</Typography>
                        <Typography {...styles.currentWarehouseValue}>
                            {`${warehouseInfo.description} - ${warehouseInfo?.address1} ${warehouseInfo.city}, ${warehouseInfo.state} ${warehouseInfo.postalCode}`}
                        </Typography>
                    </StyledWrapper>
                )}
                <GridContainer {...styles.inventoryInfoContainer}>
                    <GridItem {...styles.statusSelectionContainer}>
                        <Select
                            {...styles.warehouseStateSelection}
                            label={translate("Stock Status")}
                            value={stockStatus}
                            onChange={event => setStockStatus(event.target.value as TStockFilter)}
                            data-test-selector="ProductAvailability_stock-status-selection"
                            labelPosition="left"
                        >
                            <option value="ALL">{translate("All Warehouses")}</option>
                            <option value="IN_STOCK">{translate("In Stock Warehouses")}</option>
                        </Select>
                    </GridItem>
                </GridContainer>
                {!isLoading && (
                    <>
                        {errorMessage ? (
                            <Typography>{errorMessage}</Typography>
                        ) : (
                            <StyledWrapper {...styles.tableContainer} ref={parentBoxRef}>
                                <DataTable
                                    {...styles.availabilityByWarehouseTable}
                                    data-test-selector="availabilityByWarehouseModal"
                                >
                                    <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                        <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                            {translate("Warehouse Name")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                            {translate("Inventory")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                            {translate("Distance To Current Warehouse")}
                                        </DataTableHeader>
                                        {isPickup && (
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Action")}
                                            </DataTableHeader>
                                        )}
                                    </DataTableHead>
                                    <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                        {filteredWarehouses?.map(warehouse => (
                                            <DataTableRow
                                                {...styles.availabilityByWarehouseTableRow}
                                                key={warehouse.name}
                                            >
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseNameCell}
                                                    data-test-selector="warehouseAvailabilityModal_Description"
                                                >
                                                    {warehouse.name} - {warehouse.description}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseTableCell}
                                                    data-test-selector="warehouseAvailabilityModal_Quantity"
                                                >
                                                    {warehouse.qty}
                                                </DataTableCell>
                                                <DataTableCell
                                                    {...styles.availabilityByWarehouseTableCell}
                                                    data-test-selector="warehouseAvailabilityModal_Distance"
                                                >
                                                    {warehouse.properties?.distanceFromCurrentWarehouse === "N/A"
                                                        ? warehouse.properties?.distanceFromCurrentWarehouse
                                                        : `${parseFloat(
                                                              warehouse.properties?.distanceFromCurrentWarehouse || "0",
                                                          ).toFixed(2)} ${translate("mi")}`}
                                                </DataTableCell>
                                                {isPickup && (
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Action"
                                                    >
                                                        <Button
                                                            {...styles.actionButton}
                                                            onClick={() =>
                                                                assignAlternateWarehouse(
                                                                    warehouse.properties?.warehouseId,
                                                                )
                                                            }
                                                            disabled={
                                                                warehouse.properties?.warehouseId === warehouseInfo?.id
                                                            }
                                                        >
                                                            {translate("Select Alternate Warehouse")}
                                                        </Button>
                                                    </DataTableCell>
                                                )}
                                            </DataTableRow>
                                        ))}
                                        {!filteredWarehouses?.length && (
                                            <DataTableRow>
                                                <DataTableCell colSpan={4}>
                                                    <Notify type="info" showClose={false} {...styles.notify}>
                                                        {siteMessage("Custom_Not_Available_In_Warehouses")}
                                                    </Notify>
                                                </DataTableCell>
                                            </DataTableRow>
                                        )}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        )}
                    </>
                )}
            </LoadingOverlay>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailabilityModal);
